import React, { useState, useEffect, useRef, useMemo } from "react";
import { Plus, X, Edit2, Trash2, Calendar, Clock, Upload, Image as ImageIcon } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

// Backend API Configuration
const API_BASE_URL = "https://backend.dlapakistan.com/api/dla-trade/sponsorship-opportunity";

const SponsorshipOpportunity = () => {
  const [posts, setPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    is_active: true
  });

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const emerald600 = "#059669";

  // Axios instance
  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com",
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 10000,
  });

  // Jodit Editor Configuration
  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Fetch data from backend
  const fetchSponsorshipData = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      
      if (response.data && Array.isArray(response.data)) {
        // Transform backend data to match frontend format
        const formattedPosts = response.data.map(post => ({
          id: post.id.toString(),
          title: post.title,
          description: post.description,
          image_url: post.image_url,
          is_active: post.is_active,
          created_at: post.created_at,
          updated_at: post.updated_at,
          date: post.date || formatDateForDisplay(post.created_at),
          time: post.time || formatTimeForDisplay(post.created_at)
        }));
        setPosts(formattedPosts);
        // Save to localStorage as backup
        localStorage.setItem("sponsorOpportunities", JSON.stringify(formattedPosts));
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Fallback to localStorage if backend is offline
      const savedPosts = localStorage.getItem("sponsorOpportunities");
      if (savedPosts) {
        try {
          const parsedPosts = JSON.parse(savedPosts);
          setPosts(parsedPosts);
        } catch (e) {
          console.error("Error parsing saved data:", e);
        }
      }
      
      if (error.response?.status !== 404) {
        await Swal.fire({
          icon: "warning",
          title: "Connection Issue",
          text: "Using cached data. Some features may not work properly.",
          confirmButtonColor: emerald600,
          timer: 2000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Format time for display
  const formatTimeForDisplay = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      return "Invalid time";
    }
  };

  // Load initial data
  useEffect(() => {
    fetchSponsorshipData();
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setForm({ 
      title: "", 
      description: "", 
      is_active: true 
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openModalForNew = () => {
    setEditingPost(null);
    setForm({ 
      title: "", 
      description: "", 
      is_active: true 
    });
    setImagePreview(null);
    setShowModal(true);
  };

  const openModalForEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title || "",
      description: post.description || "",
      is_active: true
    });
    
    if (post.image_url) {
      setImagePreview(post.image_url);
    } else {
      setImagePreview(null);
    }
    
    setShowModal(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please upload an image file (JPG, PNG, GIF, etc.)",
        confirmButtonColor: emerald600
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Image size should be less than 5MB",
        confirmButtonColor: emerald600
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setImagePreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const removeImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!form.title.trim() || !form.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all required fields before saving.",
        confirmButtonColor: emerald600
      });
      return;
    }

    try {
      setSaving(true);

      const dataToSend = {
        title: form.title.trim(),
        description: form.description.trim(),
        is_active: true
      };

      if (imagePreview && imagePreview.startsWith('data:image')) {
        dataToSend.image_base64 = imagePreview;
      } else if (!imagePreview && editingPost?.image_url) {
        // If editing and image is removed, send empty string to delete image
        dataToSend.image_base64 = '';
      }

      let response;
      
      if (editingPost && editingPost.id) {
        // Update existing post
        response = await api.put(`${API_BASE_URL}/${editingPost.id}/`, dataToSend);
      } else {
        // Create new post
        response = await api.post(API_BASE_URL + "/", dataToSend);
      }

      await Swal.fire({ 
        icon: "success", 
        title: "Saved Successfully!", 
        text: editingPost ? "Post updated." : "New post created.",
        showConfirmButton: false, 
        timer: 1500 
      });

      closeModal();
      await fetchSponsorshipData();

    } catch (error) {
      console.error("Save error:", error);
      let errorMessage = "Failed to save content. Please try again.";
      
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (typeof errorData === 'object') {
            const errors = Object.entries(errorData)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('\n');
            errorMessage = `Validation errors:\n${errors}`;
          } else {
            errorMessage = `Bad request: ${errorData}`;
          }
        } else if (error.response.status === 401) {
          errorMessage = "Unauthorized. Please check if you need authentication.";
        } else if (error.response.status === 403) {
          errorMessage = "Permission denied. You don't have access to perform this action.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
        
        // Fallback to localStorage
        await saveToLocalStorage();
        return;
      }
      
      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: errorMessage,
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  // Fallback function to save to localStorage
  const saveToLocalStorage = async () => {
    const newPost = {
      id: editingPost ? editingPost.id : Date.now().toString(),
      title: form.title.trim(),
      description: form.description.trim(),
      image_url: imagePreview || (editingPost?.image_url ? editingPost.image_url : null),
      is_active: true,
      created_at: editingPost ? editingPost.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString(),
      date: formatDateForDisplay(new Date().toISOString()),
      time: formatTimeForDisplay(new Date().toISOString())
    };

    let updatedPosts;
    if (editingPost) {
      updatedPosts = posts.map(post => 
        post.id === editingPost.id ? newPost : post
      );
    } else {
      updatedPosts = [newPost, ...posts];
    }

    setPosts(updatedPosts);
    localStorage.setItem("sponsorOpportunities", JSON.stringify(updatedPosts));
    
    await Swal.fire({ 
      icon: "success", 
      title: "Saved Locally!", 
      text: "Content saved to local storage (backend offline).",
      showConfirmButton: false, 
      timer: 1500 
    });
    
    closeModal();
  };

  const handleDelete = async (postId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This post will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Try to delete from backend
          await api.delete(`${API_BASE_URL}/${postId}/`);
          
          // Update local state
          const updatedPosts = posts.filter(post => post.id !== postId);
          setPosts(updatedPosts);
          localStorage.setItem("sponsorOpportunities", JSON.stringify(updatedPosts));
          
          Swal.fire({
            title: "Deleted!",
            text: "Post has been deleted.",
            icon: "success",
            confirmButtonColor: "#059669",
            timer: 1500
          });
        } catch (error) {
          console.error("Delete error:", error);
          // Fallback to localStorage if backend fails
          const updatedPosts = posts.filter(post => post.id !== postId);
          setPosts(updatedPosts);
          localStorage.setItem("sponsorOpportunities", JSON.stringify(updatedPosts));
          
          Swal.fire({
            title: "Deleted Locally!",
            text: "Post deleted from local storage.",
            icon: "success",
            confirmButtonColor: "#059669",
            timer: 1500
          });
        }
      }
    });
  };

  const handleEditorChange = (newContent) => {
    setForm(prev => ({
      ...prev,
      description: newContent
    }));
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER - ALIGNED TO LEFT */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Sponsorship Opportunity</h1>
            <p className="text-gray-600 mt-1">Create and manage sponsorship opportunity posts</p>
          </div>
          <button
            onClick={openModalForNew}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            <Plus className="w-5 h-5 mr-2" /> Add New Post
          </button>
        </div>
      </div>

      {/* CONTENT AREA - ALL POSTS DISPLAYED */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {posts.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg mb-2">No posts added yet.</p>
            <p className="text-gray-400 text-sm">Click "Add New Post" to create your first post</p>
          </div>
        ) : (
          <div className="space-y-8">
            {posts.map((post, index) => (
              <div key={post.id} className="animate-fadeIn w-full bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                
                {/* TOP SECTION: IMAGE ON LEFT, TITLE AND ACTIONS ON RIGHT */}
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  
                  {/* SMALL IMAGE ON TOP LEFT - FITS IMAGE SIZE */}
                  {post.image_url && (
                    <div className="w-full md:w-auto">
                      <div className="border border-gray-200 rounded-lg overflow-hidden max-w-xs">
                        <img 
                          src={post.image_url}
                          alt="Post" 
                          className="max-h-60 w-auto"
                          style={{ maxWidth: '250px' }}
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/250x200?text=Image+Not+Found";
                            e.target.alt = "Image not available";
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* TITLE AND ACTIONS ON RIGHT */}
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="text-left">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                          {post.title || "Untitled Post"}
                        </h2>
                        
                        {/* DATE AND TIME INFO */}
                        <div className="flex items-center gap-4 text-gray-500 text-sm mt-2">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{post.date || formatDate(post.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{post.time || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* ACTION BUTTONS */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openModalForEdit(post)}
                          className="flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg font-medium hover:bg-emerald-100 transition-all"
                        >
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition-all"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* DESCRIPTION BELOW - Full width */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div 
                    className="text-gray-700 leading-relaxed custom-description w-full text-left" 
                    dangerouslySetInnerHTML={{ __html: post.description || "" }} 
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL FOR ADDING/EDITING POSTS */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingPost ? "Edit Post" : "Add New Post"}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                
                {/* TITLE */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Post Title <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" 
                    placeholder="e.g., Exclusive Partnership Opportunity" 
                    required 
                    disabled={saving}
                  />
                </div>
                
                {/* IMAGE UPLOAD */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Featured Image (Optional)
                  </label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-4 p-4 border-2 border-dashed border-emerald-200 rounded-2xl bg-emerald-50/50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-emerald-700">Image Preview</span>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove Image
                        </button>
                      </div>
                      <div className="flex justify-center">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-48 rounded-lg object-contain"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Upload Area */}
                  <div 
                    onClick={triggerFileInput}
                    className={`border-2 border-dashed ${imagePreview ? 'border-gray-300' : 'border-emerald-300'} rounded-2xl p-8 text-center cursor-pointer transition-all hover:bg-gray-50/50`}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                      disabled={saving}
                    />
                    <Upload className={`w-12 h-12 mx-auto mb-4 ${imagePreview ? 'text-gray-400' : 'text-emerald-500'}`} />
                    <p className="text-gray-700 font-medium mb-1">
                      {imagePreview ? 'Change Image' : 'Upload Featured Image'}
                    </p>
                    <p className="text-gray-500 text-sm">
                      Click to upload (Max 5MB)
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      PNG, JPG, GIF, SVG recommended
                    </p>
                  </div>
                </div>
                
                {/* DESCRIPTION */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Detailed Description <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.description}
                      config={config}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== form.description) {
                          setForm(prev => ({...prev, description: newContent}));
                        }
                      }}
                    />
                  </div>
                </div>
                
                {/* SUBMIT BUTTON */}
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : editingPost ? (
                    'Update Post'
                  ) : (
                    'Create Post'
                  )}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        /* Clean, simple text styling - LEFT ALIGNED */
        .custom-description * { 
          background-color: transparent !important; 
          border: none !important; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description {
          text-align: left;
        }
        
        .custom-description p {
          font-size: 1.125rem;
          line-height: 1.75;
          color: #374151;
          text-align: left;
        }
        
        .custom-description h1, 
        .custom-description h2, 
        .custom-description h3, 
        .custom-description h4, 
        .custom-description h5, 
        .custom-description h6 {
          font-weight: bold;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #111827;
          text-align: left;
        }
        
        .custom-description h1 { font-size: 2.25rem; }
        .custom-description h2 { font-size: 1.875rem; }
        .custom-description h3 { font-size: 1.5rem; }
        .custom-description h4 { font-size: 1.25rem; }
        .custom-description h5 { font-size: 1.125rem; }
        .custom-description h6 { font-size: 1rem; }
        
        .custom-description ul { 
          list-style-type: disc; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description ol { 
          list-style-type: decimal; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description li {
          margin-bottom: 0.5rem;
          text-align: left;
        }
        
        .custom-description strong, 
        .custom-description b {
          font-weight: bold;
        }
        
        .custom-description em, 
        .custom-description i {
          font-style: italic;
        }
        
        .custom-description u {
          text-decoration: underline;
        }
        
        .custom-description a {
          color: #059669;
          text-decoration: underline;
        }
        
        .custom-description a:hover {
          color: #047857;
        }
        
        .custom-description blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #6b7280;
          text-align: left;
        }
        
        .custom-description img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
        
        .custom-description table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          text-align: left;
        }
        
        .custom-description table th,
        .custom-description table td {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          text-align: left;
        }
        
        .custom-description table th {
          background-color: #f9fafb;
          font-weight: bold;
        }
        
        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 300px;
          border-radius: 16px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-radius: 16px 16px 0 0;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 20px !important;
          min-height: 200px;
          border-radius: 0 0 16px 16px;
        }
      `}</style>
    </div>
  );
};

export default SponsorshipOpportunity;